import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PLATFORM_OPTIONS, getPlatformMeta, CATEGORY_LABELS } from "@/lib/socialPlatforms";
import { Loader2, Plus, Trash2, ExternalLink, GripVertical, Save } from "lucide-react";

export default function SocialManager() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const [draft, setDraft] = useState({
        platform: "facebook",
        label: "",
        url: "",
        category: "social",
        order: 0,
    });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/social");
            setItems(data);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const onPlatformChange = (e) => {
        const platform = e.target.value;
        const meta = getPlatformMeta(platform);
        setDraft((d) => ({ ...d, platform, category: meta.category }));
    };

    const onAdd = async (e) => {
        e.preventDefault();
        if (!draft.url.trim()) {
            setError("Podaj URL.");
            return;
        }
        setSubmitting(true);
        setError("");
        try {
            await api.post("/social", draft);
            setDraft({
                platform: draft.platform,
                label: "",
                url: "",
                category: draft.category,
                order: 0,
            });
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const onUpdate = async (id, patch) => {
        try {
            await api.put(`/social/${id}`, patch);
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Usunąć link?")) return;
        try {
            await api.delete(`/social/${id}`);
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const socials = items.filter((i) => i.category === "social" || !i.category);
    const contacts = items.filter((i) => i.category === "kontakt");
    const supports = items.filter((i) => i.category === "support");

    return (
        <div data-testid="admin-social" className="grid lg:grid-cols-12 gap-8">
            <form
                onSubmit={onAdd}
                data-testid="social-form"
                className="panel-yellow p-6 lg:col-span-5 h-fit"
            >
                <div className="label-eyebrow mb-3">Dodaj link</div>

                <Field label="Platforma">
                    <select
                        data-testid="social-platform-select"
                        value={draft.platform}
                        onChange={onPlatformChange}
                        className="input"
                    >
                        <optgroup label="Social media">
                            {PLATFORM_OPTIONS.filter((p) => p.category === "social").map(
                                (p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.label}
                                    </option>
                                )
                            )}
                        </optgroup>
                        <optgroup label="Kontakt">
                            {PLATFORM_OPTIONS.filter((p) => p.category === "kontakt").map(
                                (p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.label}
                                    </option>
                                )
                            )}
                        </optgroup>
                        <optgroup label="Wsparcie">
                            {PLATFORM_OPTIONS.filter((p) => p.category === "support").map(
                                (p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.label}
                                    </option>
                                )
                            )}
                        </optgroup>
                    </select>
                </Field>

                <Field label="Etykieta (opcjonalna)">
                    <input
                        data-testid="social-label-input"
                        value={draft.label}
                        onChange={(e) =>
                            setDraft((d) => ({ ...d, label: e.target.value }))
                        }
                        placeholder={getPlatformMeta(draft.platform).label}
                        className="input"
                    />
                </Field>

                <Field label="URL *">
                    <input
                        data-testid="social-url-input"
                        value={draft.url}
                        onChange={(e) =>
                            setDraft((d) => ({ ...d, url: e.target.value }))
                        }
                        placeholder="https://..."
                        required
                        className="input"
                    />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                    <Field label="Kategoria">
                        <select
                            data-testid="social-category-select"
                            value={draft.category}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, category: e.target.value }))
                            }
                            className="input"
                        >
                            <option value="social">Social media</option>
                            <option value="kontakt">Kontakt</option>
                            <option value="support">Wsparcie</option>
                        </select>
                    </Field>
                    <Field label="Kolejność">
                        <input
                            type="number"
                            data-testid="social-order-input"
                            value={draft.order}
                            onChange={(e) =>
                                setDraft((d) => ({
                                    ...d,
                                    order: parseInt(e.target.value || "0", 10),
                                }))
                            }
                            className="input"
                        />
                    </Field>
                </div>

                {error && (
                    <div data-testid="social-error" className="text-sm text-red-400 mb-3">
                        {error}
                    </div>
                )}

                <button
                    data-testid="social-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full mt-2 disabled:opacity-60"
                >
                    {submitting ? (
                        <Loader2 className="animate-spin" size={14} />
                    ) : (
                        <Plus size={14} />
                    )}
                    Dodaj
                </button>
                <style>{`
                    .input { width:100%; background: var(--bg-base); border:1px solid rgba(255,255,255,0.1); border-radius:6px; padding:8px 12px; color:white; font-size:14px; }
                    .input:focus { outline:none; border-color: var(--primary); }
                `}</style>
            </form>

            <div className="lg:col-span-7 space-y-8">
                <LinkGroup
                    title={`Social media (${socials.length})`}
                    items={socials}
                    loading={loading}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                />
                <LinkGroup
                    title={`Kontakt (${contacts.length})`}
                    items={contacts}
                    loading={loading}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                />
                <LinkGroup
                    title={`Wsparcie (${supports.length})`}
                    items={supports}
                    loading={loading}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                />
            </div>
        </div>
    );
}

function LinkGroup({ title, items, loading, onUpdate, onDelete }) {
    return (
        <div>
            <div className="label-eyebrow mb-3">{title}</div>
            {loading ? (
                <Loader2 className="animate-spin text-[color:var(--text-secondary)]" />
            ) : items.length === 0 ? (
                <div className="text-sm text-[color:var(--text-secondary)] panel-yellow p-6 text-center">
                    Brak linków.
                </div>
            ) : (
                <ul className="space-y-2">
                    {items.map((link) => (
                        <SocialRow
                            key={link.id}
                            link={link}
                            onUpdate={onUpdate}
                            onDelete={onDelete}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
}

function SocialRow({ link, onUpdate, onDelete }) {
    const meta = getPlatformMeta(link.platform);
    const Icon = meta.Icon;
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({
        label: link.label || "",
        url: link.url,
        order: link.order ?? 0,
    });

    const save = async () => {
        await onUpdate(link.id, draft);
        setEditing(false);
    };

    return (
        <li
            data-testid={`social-row-${link.id}`}
            className="panel-yellow p-4 flex items-center gap-3"
            style={{ borderColor: `${meta.color}30` }}
        >
            <GripVertical
                size={14}
                className="text-[color:var(--text-muted)] shrink-0"
            />
            <div
                className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                style={{ background: `${meta.color}22`, color: meta.color }}
            >
                <Icon size={16} />
            </div>
            <div className="flex-1 min-w-0">
                {editing ? (
                    <div className="space-y-2">
                        <input
                            data-testid={`social-edit-label-${link.id}`}
                            value={draft.label}
                            placeholder={meta.label}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, label: e.target.value }))
                            }
                            className="w-full bg-[color:var(--bg-base)] border border-white/10 rounded-md px-2 py-1 text-sm text-white"
                        />
                        <input
                            data-testid={`social-edit-url-${link.id}`}
                            value={draft.url}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, url: e.target.value }))
                            }
                            className="w-full bg-[color:var(--bg-base)] border border-white/10 rounded-md px-2 py-1 text-sm text-white"
                        />
                    </div>
                ) : (
                    <>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-white font-medium">
                                {link.label || meta.label}
                            </span>
                            <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-muted)]">
                                · #{link.order ?? 0}
                            </span>
                        </div>
                        <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-[color:var(--text-secondary)] hover:text-[color:var(--primary)] truncate inline-flex items-center gap-1 max-w-full"
                        >
                            {link.url}
                            <ExternalLink size={11} />
                        </a>
                    </>
                )}
            </div>
            {editing ? (
                <button
                    data-testid={`social-save-${link.id}`}
                    onClick={save}
                    className="text-xs uppercase tracking-wider px-3 py-2 rounded-md bg-[color:var(--primary)] text-[color:var(--bg-base)] inline-flex items-center gap-1.5"
                >
                    <Save size={12} /> Zapisz
                </button>
            ) : (
                <button
                    data-testid={`social-edit-${link.id}`}
                    onClick={() => setEditing(true)}
                    className="text-xs uppercase tracking-wider text-[color:var(--text-secondary)] hover:text-white px-2"
                >
                    Edytuj
                </button>
            )}
            <button
                data-testid={`social-delete-${link.id}`}
                onClick={() => onDelete(link.id)}
                className="text-red-400 hover:text-red-300 p-2"
                aria-label="Usuń"
            >
                <Trash2 size={14} />
            </button>
        </li>
    );
}

function Field({ label, children }) {
    return (
        <label className="block mb-3">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                {label}
            </span>
            {children}
        </label>
    );
}
